"use client";

import Container from "@/app/components/ui/container";
import { Button } from "@/app/components/ui/button";
import StatusTag from "@/app/components/ui/status-tag";
import VehicleDetailsList from "@/app/components/ui/vehicle-details-list";
import { getOfferTypeClassification } from "@/app/utils/helpers/backend/offers/get-offer-type-classification";
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import Image from "next/image";
import * as Sentry from "@sentry/nextjs";
import HighlightTag from "@/app/components/ui/highlight-tag";
import Subheading from "@/app/components/ui/subheading";
import OfferCard from "@/app/components/offers/offer-card";
import { Badge } from "@/app/components/ui/badge";
import { Breadcrumb } from "../ui/breadcrumb";
import Link from "next/link";
import { ListingSlider } from "@/app/(pages)/(frontend)/t/[tenant]/startseite/components/listing-slider";
import { getFinancingDisclaimer } from "@/app/utils/helpers/backend/offers/get-financing-disclaimer";
import { getLeasingTransferCostNote } from "@/app/utils/helpers/backend/offers/get-leasing-transfer-cost-note";
import { getOfferListPriceSavingsDisclaimer } from "@/app/utils/helpers/backend/offers/get-offer-list-price-savings";
import { useOfferVehicleDetails } from "@/app/components/offers/hooks/use-offer-vehicle-details";
import { getStatusTag } from "@/app/components/offers/utils/formatters";
import OfferImageGallery from "./offer-image-gallery";
import {
  GalleryLightbox,
  type GalleryImageT,
} from "@/app/components/ui/image-gallery/gallery-lightbox";
import OfferValidUntil from "./offer-valid-until";
import VehiclePricingDetails from "./vehicle-pricing-details";
import VehicleConsumptionDetails from "./vehicle-consumption-details";
import VehicleTitleSection from "./vehicle-title-section";
import ListPriceSavingsBadge from "./list-price-savings-badge";
import {
  getConsumptionText,
  getEfficiencyLabel,
  getHybridEfficiencyLabels,
  getPriceText,
} from "@/app/utils/helpers/backend/offers/offer-card/offer-card-helpers";
import OfferLocationList from "./offer-location-list";
import { ContactFormVariant } from "@/app/components/forms/contact/contact-form-variant";
import type { OfferFormDataT, RelatedOfferMinimalT } from "@/app/types/offers";
import type { CompanyPositionT } from "@/app/types/companies";
import type { CampaignLocationT } from "./campaign-location-list";
import type { ContactFormSelectOptionT } from "@/app/components/forms/contact/contact-form-types";
import {
  getContactFormLocationLabel,
  resolveLockedContactFormLocationOption,
} from "@/app/components/forms/contact/contact-form-location-options";
import { buildBrandPath, type BrandKeyT } from "@/app/config/brand-registry";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { cn } from "@/lib/utils";

const EQUIPMENT_VISIBLE_COUNT = 15;
const EQUIPMENT_COLLAPSE_DELAY_MS = 140;

interface OfferPageContentProps {
  offer: OfferFormDataT;
  relatedOffers?: RelatedOfferMinimalT[];
  contactLocations?: CampaignLocationT[];
  locationSelectOptions: ContactFormSelectOptionT[];
  makeSelectOptions: ContactFormSelectOptionT[];
  companyContext?: {
    companyId: string;
    companyName: string;
    companySlug: string;
    companyPosition: CompanyPositionT;
  };
  brandKey?: BrandKeyT;
}

export default function PublicOfferPageContent({
  offer,
  relatedOffers = [],
  contactLocations = [],
  locationSelectOptions,
  makeSelectOptions,
  companyContext,
  brandKey = "default",
}: OfferPageContentProps) {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [initialImageIndex, setInitialImageIndex] = useState(0);
  const [showAllHighlights, setShowAllHighlights] = useState(false);
  const [showAllSonderausstattung, setShowAllSonderausstattung] =
    useState(false);
  const [showAllSerienausstattung, setShowAllSerienausstattung] =
    useState(false);
  const highlightsSectionRef = useRef<HTMLDivElement | null>(null);
  const sonderausstattungSectionRef = useRef<HTMLDivElement | null>(null);
  const serienausstattungSectionRef = useRef<HTMLDivElement | null>(null);
  const collapseTimeoutRef = useRef<number | null>(null);
  const lockedLocationOption = resolveLockedContactFormLocationOption(
    locationSelectOptions
  );
  const { isSkoda, isAudi } = useBrand();

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: "page",
      message: "offer-page-hydrated",
      level: "info",
      data: {
        offerId: offer.id,
        offerSlug: offer.slug,
        pathname: typeof window !== "undefined" ? window.location.pathname : "",
      },
    });
  }, [offer.id, offer.slug]);

  useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current !== null) {
        window.clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, []);

  const additionalImagesWithUrl = (offer.additional_images ?? []).filter(
    (image) => Boolean(image?.url)
  );

  const sortedAdditionalImages = [...additionalImagesWithUrl].sort(
    (a, b) =>
      (a.position ?? Number.MAX_SAFE_INTEGER) -
      (b.position ?? Number.MAX_SAFE_INTEGER)
  );

  // Get additional images for gallery slots
  const additionalImage1 = sortedAdditionalImages[0];
  const additionalImage2 = sortedAdditionalImages[1];
  const additionalImage3 = sortedAdditionalImages[2];
  const additionalImage4 = sortedAdditionalImages[3];

  // Create complete image gallery array (all images in correct order)
  const galleryImages = [
    ...(offer.thumbnail_image?.url
      ? [
          {
            id: offer.thumbnail_image.id,
            url: offer.thumbnail_image.url,
            alt_text: offer.thumbnail_image.alt_text || offer.title,
            type: "thumbnail" as const,
          },
        ]
      : []),
    ...sortedAdditionalImages.map((img) => ({
      id: img.id,
      url: img.url || "",
      alt_text: img.alt_text || "Fahrzeugbild",
      type: "additional" as const,
    })),
    ...(offer.interieur_image?.url
      ? [
          {
            id: offer.interieur_image.id,
            url: offer.interieur_image.url,
            alt_text: offer.interieur_image.alt_text || "Innenraum",
            type: "interior" as const,
          },
        ]
      : []),
  ].filter((image) => Boolean(image.url));

  // Check if we have any images to show gallery
  const hasGalleryImages = galleryImages.length > 0;

  // Transform gallery images to GalleryLightbox format
  const galleryLightboxImages: GalleryImageT[] = galleryImages.map((img) => ({
    src: img.url,
    alt: img.alt_text || undefined,
    title: img.alt_text || undefined,
  }));

  // Function to open gallery with specific image index
  const openGallery = (index: number = 0) => {
    setInitialImageIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
    setInitialImageIndex(0);
  };

  // Find index of image in gallery by URL
  const findImageIndex = (imageUrl: string | null | undefined): number => {
    if (!imageUrl) return 0;
    const index = galleryImages.findIndex((img) => img.url === imageUrl);
    return index >= 0 ? index : 0;
  };

  const primaryCampaign = offer.campaigns?.[0];
  const vehicleDetails = useOfferVehicleDetails(offer);
  const annualMileage = offer.pricing?.leasing?.jaehrlicheFahrleistung ?? null;
  const offerPriceText = getPriceText(offer);
  const offerPriceLine =
    typeof offerPriceText === "string"
      ? offerPriceText
      : `${offerPriceText.pricingLabel} ${offerPriceText.price}`;
  const offerPriceVatLabel =
    typeof offerPriceText === "string" ? undefined : offerPriceText.vatLabel;

  // Get appropriate financing disclaimer based on offer types
  const financingDisclaimer = getFinancingDisclaimer({
    make: offer.make ?? null, // Ensure null-safe access to disclaimer fields.
    offerTypes: offer.offer_types,
  });
  const listPriceSavingsDisclaimer = getOfferListPriceSavingsDisclaimer(offer);
  const leasingTransferCostNote = getLeasingTransferCostNote(offer.pricing);
  const hasFinancingFootnote =
    Boolean(financingDisclaimer) && offer.pricing?.type !== "barkauf";
  const listPriceSavingsFootnoteMarker = hasFinancingFootnote ? "²" : "¹";

  // Transform related offers to ListingSlider format
  const relatedOfferListings = useMemo(() => {
    return relatedOffers.map((relatedOffer) => {
      const offerTypes = Array.isArray(relatedOffer.offer_types)
        ? relatedOffer.offer_types
            .map((type) => type?.label)
            .filter((label): label is string => Boolean(label))
        : [];

      return {
        id: relatedOffer.id,
        image: relatedOffer.thumbnail_image?.url || "",
        title: relatedOffer.title || "Weiteres Angebot",
        listingCard: (
          <OfferCard
            image={relatedOffer.thumbnail_image?.url || undefined}
            title={relatedOffer.title || ""}
            efficiency={getConsumptionText(relatedOffer as OfferFormDataT)}
            price={getPriceText(relatedOffer as OfferFormDataT)}
            efficiencyLabel={getEfficiencyLabel(relatedOffer as OfferFormDataT)}
            hybridLabels={getHybridEfficiencyLabels(
              relatedOffer as OfferFormDataT
            )}
            offer_types={offerTypes}
            listPriceSavingsBadge={
              <ListPriceSavingsBadge
                offer={{
                  pricing: relatedOffer.pricing,
                  offer_types: relatedOffer.offer_types,
                }}
              />
            }
            href={
              relatedOffer.slug
                ? buildBrandPath(brandKey, `/angebote/${relatedOffer.slug}`)
                : undefined
            }
          />
        ),
      };
    });
  }, [relatedOffers]);

  const visibleHighlights = showAllHighlights
    ? offer.highlights
    : offer.highlights?.slice(0, EQUIPMENT_VISIBLE_COUNT);
  const visibleSonderausstattung = showAllSonderausstattung
    ? offer.sonderausstattung
    : offer.sonderausstattung?.slice(0, EQUIPMENT_VISIBLE_COUNT);
  const visibleSerienausstattung = showAllSerienausstattung
    ? offer.serienausstattung
    : offer.serienausstattung?.slice(0, EQUIPMENT_VISIBLE_COUNT);
  const hasMoreHighlights =
    (offer.highlights?.length ?? 0) > EQUIPMENT_VISIBLE_COUNT;
  const hasMoreSonderausstattung =
    (offer.sonderausstattung?.length ?? 0) > EQUIPMENT_VISIBLE_COUNT;
  const hasMoreSerienausstattung =
    (offer.serienausstattung?.length ?? 0) > EQUIPMENT_VISIBLE_COUNT;
  const hiddenHighlights = offer.highlights?.slice(EQUIPMENT_VISIBLE_COUNT);
  const hiddenSonderausstattung = offer.sonderausstattung?.slice(
    EQUIPMENT_VISIBLE_COUNT
  );
  const hiddenSerienausstattung = offer.serienausstattung?.slice(
    EQUIPMENT_VISIBLE_COUNT
  );

  const collapseEquipmentSection = (
    ref: RefObject<HTMLDivElement | null>,
    onCollapse: () => void
  ) => {
    if (collapseTimeoutRef.current !== null) {
      window.clearTimeout(collapseTimeoutRef.current);
    }

    ref.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    collapseTimeoutRef.current = window.setTimeout(() => {
      onCollapse();
      collapseTimeoutRef.current = null;
    }, EQUIPMENT_COLLAPSE_DELAY_MS);
  };

  return (
    <div className={cn(isSkoda && "bg-muted", isAudi && "bg-background")}>
      {(isSkoda || isAudi) && (
        <div>
          <Breadcrumb listClassName="py-2" />
        </div>
      )}
      <Container>
        {!isSkoda && !isAudi && (
          <div className="pt-4">
            <Breadcrumb listClassName="py-2" />
          </div>
        )}

        {/* Main image and gallery */}
        <OfferImageGallery
          thumbnailImage={offer.thumbnail_image}
          thumbnailAlt={offer.thumbnail_image?.alt_text || offer.title || null}
          additionalImage1={additionalImage1}
          additionalImage2={additionalImage2}
          additionalImage3={additionalImage3}
          additionalImage4={additionalImage4}
          interieurImage={offer.interieur_image}
          onImageClick={(imageUrl) => openGallery(findImageIndex(imageUrl))}
          showGalleryButton={hasGalleryImages}
          onGalleryButtonClick={() => openGallery(0)}
        />

        <div
          id="price-and-features"
          className="grid grid-cols-1 pt-8 lg:grid-cols-9 lg:grid-rows-[auto] lg:items-start lg:gap-x-8"
        >
          {/* Heading and description */}
          <div
            id="content"
            className={cn(
              "flex flex-col gap-2 lg:col-span-6 lg:row-start-1 lg:row-end-1",
              isSkoda &&
                "rounded-none border-0 bg-white p-4 shadow-[5px_10px_35px_hsl(0_0%_20%_/_0.15)] lg:p-6"
            )}
          >
            {primaryCampaign && (
              <Badge asChild className="self-start">
                <Link
                  href={buildBrandPath(
                    brandKey,
                    `/aktionen/${primaryCampaign.slug}`
                  )}
                >
                  {primaryCampaign.title}
                </Link>
              </Badge>
            )}
            <VehicleTitleSection
              title={offer.title}
              modelName={offer.model_name}
              consumption={offer.consumption}
            />
          </div>

          {/* Price and Features */}
          <div className="relative h-full lg:col-span-3 lg:col-start-7 lg:row-start-1 lg:row-end-6">
            <div
              className={cn(
                "sticky top-8 mt-8 rounded-lg border-2 p-4 lg:mt-0 lg:p-8",
                isSkoda &&
                  "rounded-none border-0 bg-white shadow-[5px_35px_35px_hsl(0_0%_40%_/_0.15)]",
                isAudi && "bg-card rounded-[20px] border-0 text-white"
              )}
            >
              <div className="flex flex-wrap items-start gap-3 pb-3 text-sm">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <StatusTag status={getStatusTag(offer)} />
                </div>
                <ListPriceSavingsBadge
                  offer={offer}
                  size="md"
                  className="ml-auto shrink-0"
                  footnoteMarker={listPriceSavingsFootnoteMarker}
                />
              </div>
              <div>
                <h2 className="text-xl font-bold lg:text-2xl">
                  Fahrzeug anfragen
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-foreground">
                    {getOfferTypeClassification(offer.offer_types)}
                  </p>
                  {offer?.valid_until && (
                    <>
                      <span className="text-foreground">-</span>
                      <OfferValidUntil
                        validUntil={offer.valid_until}
                        className="text-foreground"
                      />
                    </>
                  )}
                </div>
              </div>

              <VehiclePricingDetails
                pricing={offer.pricing}
                annualMileage={annualMileage}
                offerTypes={offer.offer_types}
                vehicleCondition={offer.vehicle_condition}
                financingFootnoteMarker={hasFinancingFootnote ? "¹" : null}
              />

              <div className="pt-6">
                <Link
                  className="w-full"
                  href="#kontakt"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById("kontakt");
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  <Button className="w-full font-semibold">
                    Jetzt anfragen
                  </Button>
                </Link>
              </div>
              {hasFinancingFootnote && financingDisclaimer && (
                <p className="pt-4 text-xs whitespace-pre-line">
                  ¹ {financingDisclaimer}
                </p>
              )}
              {listPriceSavingsDisclaimer && (
                <p className="pt-2 text-xs whitespace-pre-line">
                  {listPriceSavingsFootnoteMarker} {listPriceSavingsDisclaimer}
                </p>
              )}
              {leasingTransferCostNote && (
                <p className="mt-3 border-t border-current/20 pt-3 text-xs whitespace-pre-line">
                  {leasingTransferCostNote}
                </p>
              )}
            </div>
          </div>

          {/* Vehicle Details */}
          <div
            id="vehicle-details"
            className={cn(
              "pt-4 lg:col-span-6 lg:col-start-1 lg:row-start-2 lg:row-end-2 lg:self-stretch",
              isSkoda &&
                "mt-6 rounded-none border-0 bg-white p-4 shadow-[5px_35px_35px_hsl(0_0%_40%_/_0.15)] lg:mt-0 lg:p-6 lg:pt-0",
              isAudi && "mt-6 lg:mt-0"
            )}
          >
            <VehicleDetailsList details={vehicleDetails} />
          </div>

          {/* Vehicle emissions */}
          <div
            className={cn(
              "pt-8 lg:col-span-6 lg:col-start-1 lg:row-start-3 lg:row-end-3",
              isSkoda &&
                "mt-6 rounded-none border-0 bg-white p-4 shadow-[5px_35px_35px_hsl(0_0%_40%_/_0.15)] lg:mt-0 lg:p-6"
            )}
          >
            <VehicleConsumptionDetails
              consumption={offer.consumption}
              consumptionFileUrl={offer.consumptionFile?.url}
            />

            {((offer.highlights && offer.highlights.length > 0) ||
              (offer.sonderausstattung && offer.sonderausstattung.length > 0) ||
              (offer.serienausstattung &&
                offer.serienausstattung.length > 0)) && (
              <div className="flex flex-row items-center justify-between gap-2 border-b pt-8 pb-3">
                <h2 className="heading-medium">Ausstattungen</h2>
                {offer.konfigurationscode && (
                  <div
                    className={cn(
                      "bg-light-blue rounded-sm px-2 py-1 text-[13px]",
                      isAudi && "bg-primary text-primary-foreground rounded-md"
                    )}
                  >
                    Konfigurationscode:
                    <span
                      className={cn(
                        "text-primary font-semibold",
                        isAudi && "text-primary-foreground"
                      )}
                    >
                      {" "}
                      {offer.konfigurationscode}
                    </span>
                  </div>
                )}
              </div>
            )}

            {offer.highlights && offer.highlights.length > 0 && (
              <div ref={highlightsSectionRef} className="pt-8">
                <h3 className="heading-medium">Ausstattungshighlights</h3>
                <div className="flex flex-row flex-wrap gap-3 pt-6">
                  {visibleHighlights
                    ?.slice(0, EQUIPMENT_VISIBLE_COUNT)
                    .map((highlight, index) => (
                      <HighlightTag key={index}>{highlight}</HighlightTag>
                    ))}
                </div>
                {hasMoreHighlights && (
                  <div
                    className={`grid transition-[grid-template-rows,opacity,padding] duration-700 ease-in-out ${
                      showAllHighlights
                        ? "grid-rows-[1fr] pt-3 opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="flex flex-row flex-wrap gap-3">
                        {hiddenHighlights?.map((highlight, index) => (
                          <HighlightTag
                            key={`${EQUIPMENT_VISIBLE_COUNT + index}`}
                          >
                            {highlight}
                          </HighlightTag>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {hasMoreHighlights && !showAllHighlights && (
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 pt-4 font-semibold"
                    onClick={() => setShowAllHighlights(true)}
                  >
                    Mehr anzeigen
                  </Button>
                )}
                {hasMoreHighlights && showAllHighlights && (
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 pt-4 font-semibold"
                    onClick={() =>
                      collapseEquipmentSection(highlightsSectionRef, () =>
                        setShowAllHighlights(false)
                      )
                    }
                  >
                    Weniger anzeigen
                  </Button>
                )}
              </div>
            )}

            {offer.sonderausstattung && offer.sonderausstattung.length > 0 && (
              <div ref={sonderausstattungSectionRef} className="pt-10">
                <h3 className="heading-medium pb-4">Sonderausstattung</h3>
                {visibleSonderausstattung
                  ?.slice(0, EQUIPMENT_VISIBLE_COUNT)
                  .map((equipment, index) => (
                    <div key={index} className="border-b pt-2.5 pb-2.5">
                      {equipment}
                    </div>
                  ))}
                {hasMoreSonderausstattung && (
                  <div
                    className={`grid transition-[grid-template-rows,opacity,padding] duration-700 ease-in-out ${
                      showAllSonderausstattung
                        ? "grid-rows-[1fr] pt-1 opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      {hiddenSonderausstattung?.map((equipment, index) => (
                        <div
                          key={`${EQUIPMENT_VISIBLE_COUNT + index}`}
                          className="border-b pt-2.5 pb-2.5"
                        >
                          {equipment}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {hasMoreSonderausstattung && !showAllSonderausstattung && (
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 pt-4 font-semibold"
                    onClick={() => setShowAllSonderausstattung(true)}
                  >
                    Mehr anzeigen
                  </Button>
                )}
                {hasMoreSonderausstattung && showAllSonderausstattung && (
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 pt-4 font-semibold"
                    onClick={() =>
                      collapseEquipmentSection(
                        sonderausstattungSectionRef,
                        () => setShowAllSonderausstattung(false)
                      )
                    }
                  >
                    Weniger anzeigen
                  </Button>
                )}
              </div>
            )}

            {offer.serienausstattung && offer.serienausstattung.length > 0 && (
              <div ref={serienausstattungSectionRef} className="pt-10">
                <h3 className="heading-medium pb-4">Serienausstattung</h3>
                {visibleSerienausstattung
                  ?.slice(0, EQUIPMENT_VISIBLE_COUNT)
                  .map((equipment, index) => (
                    <div key={index} className="border-b pt-2.5 pb-2.5">
                      {equipment}
                    </div>
                  ))}
                {hasMoreSerienausstattung && (
                  <div
                    className={`grid transition-[grid-template-rows,opacity,padding] duration-700 ease-in-out ${
                      showAllSerienausstattung
                        ? "grid-rows-[1fr] pt-1 opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      {hiddenSerienausstattung?.map((equipment, index) => (
                        <div
                          key={`${EQUIPMENT_VISIBLE_COUNT + index}`}
                          className="border-b pt-2.5 pb-2.5"
                        >
                          {equipment}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {hasMoreSerienausstattung && !showAllSerienausstattung && (
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 pt-4 font-semibold"
                    onClick={() => setShowAllSerienausstattung(true)}
                  >
                    Mehr anzeigen
                  </Button>
                )}
                {hasMoreSerienausstattung && showAllSerienausstattung && (
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 pt-4 font-semibold"
                    onClick={() =>
                      collapseEquipmentSection(
                        serienausstattungSectionRef,
                        () => setShowAllSerienausstattung(false)
                      )
                    }
                  >
                    Weniger anzeigen
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
      <div className="container-default mx-auto">
        {relatedOfferListings.length > 0 && (
          <div className="pt-12 pb-16 lg:pt-24 lg:pb-30">
            <ListingSlider
              heading={
                <div>
                  <Subheading>Leasingangebote</Subheading>
                  <h2 className="heading-large">Weitere Angebote</h2>
                  <p>Entdecken Sie unsere weiteren Angebote & Aktionen.</p>
                </div>
              }
              listings={relatedOfferListings}
              listingCard={null}
            />
          </div>
        )}
      </div>
      <div id="kontakt" className="bg-muted py-6 pb-20">
        <Container>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Left column - Title, text, vehicle info and locations */}
            <div className="flex flex-col gap-6">
              {/* Title and description */}
              <div>
                <Subheading>Interesse?</Subheading>
                <h2 className="heading-large">Angebot anfragen</h2>
                <p className="pt-1">
                  Schicken Sie uns gerne eine Anfrage. Ein Verkaufsberater wird
                  sich so schnell wie möglich bei Ihnen melden.
                </p>
              </div>

              {/* Vehicle card */}
              <div
                className={cn(
                  "bg-background overflow-hidden rounded-lg shadow-lg",
                  isSkoda &&
                    "rounded-none shadow-[0_6px_16px_hsl(0_0%_20%_/_0.10)]",
                  isAudi && "bg-card rounded-[20px] shadow-none"
                )}
              >
                <div className="flex">
                  {offer.thumbnail_image?.url ? (
                    <Image
                      alt={offer.thumbnail_image.alt_text || offer.title || ""}
                      src={offer.thumbnail_image.url}
                      width={120}
                      height={90}
                      className="h-[90px] w-[120px] object-cover"
                    />
                  ) : (
                    <div className="bg-muted flex h-[90px] w-[120px] items-center justify-center">
                      <p className="text-muted-foreground text-xs">Bild</p>
                    </div>
                  )}
                  <div className="flex flex-1 flex-col justify-center px-4 py-3">
                    <h3 className="text-sm font-semibold">{offer.title}</h3>
                    {offerPriceLine && (
                      <p className="text-sm font-medium">
                        {offerPriceLine}
                        {offerPriceVatLabel && (
                          <span className="text-secondary-foreground ml-2 text-xs font-normal">
                            {offerPriceVatLabel}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Locations section */}
              {contactLocations.length > 0 && (
                <div className="mt-4">
                  <h3 className="heading-medium">Unsere Standorte</h3>
                  <p className="text-muted-foreground mb-6 text-sm">
                    Hier finden Sie den direkten Kontakt oder auch die Adressen
                    unserer Standorte.
                  </p>

                  <OfferLocationList locations={contactLocations} />
                </div>
              )}
            </div>

            {/* Right column - Contact form */}
            {companyContext ? (
              <ContactFormVariant
                variant="companyOffer"
                className={cn(
                  isSkoda &&
                    "rounded-none shadow-[0_6px_16px_hsl(0_0%_20%_/_0.10)]",
                  isAudi && "bg-card rounded-[20px] shadow-none"
                )}
                variantContext={{
                  companyId: companyContext.companyId,
                  companyName: companyContext.companyName,
                  slug: companyContext.companySlug,
                  position: companyContext.companyPosition,
                  vehicleCondition:
                    offer.vehicle_condition === "new"
                      ? "Neuwagen"
                      : offer.vehicle_condition === "used"
                        ? "Gebrauchtwagen"
                        : null,
                  offer: {
                    id: offer.id,
                    title: offer.title,
                    make: offer.make?.name || null,
                    model: offer.model?.name || null,
                  },
                }}
              />
            ) : (
              <ContactFormVariant
                variant="offer"
                className={cn(
                  isSkoda &&
                    "rounded-none shadow-[0_6px_16px_hsl(0_0%_20%_/_0.10)]",
                  isAudi && "bg-card rounded-[20px] shadow-none"
                )}
                runtimeSelectOptions={{
                  location: lockedLocationOption
                    ? [lockedLocationOption]
                    : locationSelectOptions,
                  makeName: makeSelectOptions,
                }}
                lockedSelectValues={
                  lockedLocationOption
                    ? { location: lockedLocationOption.value }
                    : undefined
                }
                variantContext={{
                  offer: {
                    id: offer.id,
                    title: offer.title,
                    make: offer.make?.name || null,
                    model: offer.model?.name || null,
                    aktion: primaryCampaign?.title || null,
                    vehicleCondition:
                      offer.vehicle_condition === "new"
                        ? "Neuwagen"
                        : offer.vehicle_condition === "used"
                          ? "Gebrauchtwagen"
                          : null,
                    location:
                      lockedLocationOption?.label ??
                      (contactLocations[0]
                        ? getContactFormLocationLabel(contactLocations[0])
                        : null),
                    locationId: lockedLocationOption?.value ?? null,
                  },
                }}
              />
            )}
          </div>
        </Container>
      </div>

      {/* Image Gallery Modal */}
      {hasGalleryImages && (
        <GalleryLightbox
          images={galleryLightboxImages}
          initialIndex={initialImageIndex}
          isOpen={isGalleryOpen}
          onClose={closeGallery}
        />
      )}
    </div>
  );
}
